/**
 * The Passport stamp vocabularies — ONE home for the values a stamp carries,
 * shared by the catalog, platform-api, the SDK and both citizen apps.
 *
 * `category` is stored in `platform.stamps.category`, seeded from the catalog
 * (`catalog/stamps.json`) and sent raw on every stamp read. The catalog spec
 * pins the two directions: every stamp's category is one of these, and every
 * one of these has a stamp.
 */

/** Where a stamp comes from: a hero, the first steps, or several heroes at once. */
export const PASSPORT_STAMP_CATEGORIES = [
  'onboarding',
  'checkup',
  'teleportal',
  'qultureup',
  'care',
  'inout',
  'cross_hero',
] as const;
export type PassportStampCategory = (typeof PASSPORT_STAMP_CATEGORIES)[number];

/**
 * The label key for each category. The hero categories render the brand
 * name, verbatim in every locale; `onboarding` and `cross_hero` translate.
 * Guarded by packages/locale/src/dynamic-key-coverage.spec.ts.
 */
export const PASSPORT_STAMP_CATEGORY_LABEL_KEY = {
  onboarding: 'passport.stampCategory.onboarding',
  checkup: 'passport.stampCategory.checkup',
  teleportal: 'passport.stampCategory.teleportal',
  qultureup: 'passport.stampCategory.qultureup',
  care: 'passport.stampCategory.care',
  inout: 'passport.stampCategory.inout',
  cross_hero: 'passport.stampCategory.cross_hero',
} as const satisfies Record<PassportStampCategory, string>;

/**
 * The conditions a stamp's trigger can hold — the keys platform-api's
 * `StampEngine.evaluateConditions` evaluates. Numeric ones are thresholds on a
 * live count (so an unearned stamp has measurable progress towards them);
 * `band` and `taskCode` are equalities on the minting event's payload.
 */
// A `type`, not an `interface`: the engine reads conditions as a
// `Record<string, unknown>`, which only a type alias is assignable to.
export type PassportStampConditions = {
  /** How many times the trigger event has happened for the talent. */
  count?: number;
  /** The iQulture band the minting event carries (e.g. 'PRO'). */
  band?: string;
  /** The TelePortal task code the minting event carries. */
  taskCode?: string;
  /** Consecutive days with a completed QultureUp mission. */
  streakDays?: number;
  /** Distinct QultureUp worlds completed, once an advanced assessment exists. */
  qultureup_worlds_advanced?: number;
  /** Months since the talent's first journey began. */
  monthsOnJourney?: number;
  /** How many of the five heroes the talent has used (an IRR event in the hero's own category). */
  heroes_activated?: number;
};

export type PassportStampConditionKey = keyof PassportStampConditions;

/**
 * Every condition key, complete by construction: the `satisfies` below fails to
 * compile when a key is added to {@link PassportStampConditions} and not here.
 */
const CONDITION_KEYS = {
  count: true,
  band: true,
  taskCode: true,
  streakDays: true,
  qultureup_worlds_advanced: true,
  monthsOnJourney: true,
  heroes_activated: true,
} as const satisfies Record<PassportStampConditionKey, true>;

export const PASSPORT_STAMP_CONDITION_KEYS = Object.keys(
  CONDITION_KEYS
) as readonly PassportStampConditionKey[];
